import React, { useState, useMemo, useEffect } from "react";
import { useLocalStorage } from "usehooks-ts";

interface GaugeChartProps {
  valueArray: number[];
  maxValue: number;
  valueType: string;
}

type Pointer = {
  color: string;
  label: string;
  value: number;
  large?: boolean;
  visible?: boolean;
};

// Constants for gauge geometry
const GAUGE_SIZE = 400;
const CENTER = GAUGE_SIZE / 2;
const OUTER_RADIUS = 160;
const INNER_RADIUS = 74;
const START_ANGLE = -110;
const END_ANGLE = 110;
const TOTAL_ANGLE = END_ANGLE - START_ANGLE;

const ticks = [0, 30, 45, 60, 75, 90, 100];

const certificationStops = [
  { color: "#e6e6e6", start: 0, stop: 30, label: "Ingen sertifisering" },
  { color: "#cccccc", start: 30, stop: 45, label: "Pass" },
  { color: "#b3b3b3", start: 45, stop: 60, label: "Good" },
  { color: "#808080", start: 60, stop: 75, label: "Very good" },
  { color: "#666666", start: 75, stop: 90, label: "Excellent" },
  { color: "#484747", start: 90, stop: 100, label: "Outstanding" },
];

// Convert percentage (0-100) to angle in radians
const percentToAngle = (percent: number): number => {
  const clampedPercent = Math.max(0, Math.min(100, percent));
  const angle = START_ANGLE + (clampedPercent / 100) * TOTAL_ANGLE;
  return (angle * Math.PI) / 180;
};

// Convert angle to x,y coordinates
const polarToCartesian = (angle: number, radius: number): { x: number; y: number } => ({
  x: CENTER + radius * Math.cos(angle - Math.PI / 2),
  y: CENTER + radius * Math.sin(angle - Math.PI / 2),
});

// Create SVG arc path
const createArc = (startPercent: number, endPercent: number, innerR: number, outerR: number): string => {
  const startAngle = percentToAngle(startPercent);
  const endAngle = percentToAngle(endPercent);

  const outerStart = polarToCartesian(startAngle, outerR);
  const outerEnd = polarToCartesian(endAngle, outerR);
  const innerStart = polarToCartesian(startAngle, innerR);
  const innerEnd = polarToCartesian(endAngle, innerR);

  const largeArc = endPercent - startPercent > 50 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    "Z",
  ].join(" ");
};

// Needle component with animation
const Needle: React.FC<{
  value: number;
  color: string;
  large?: boolean;
  onHover?: (hovering: boolean) => void;
}> = ({ value, color, large, onHover }) => {
  const targetAngle = percentToAngle(value);
  const startAngle = percentToAngle(0);
  const needleLength = OUTER_RADIUS - 15;
  const needleWidth = 16;
  // Convert radians to degrees for CSS rotation
  const targetDeg = (targetAngle * 180) / Math.PI;
  const startDeg = (startAngle * 180) / Math.PI;
  const [rotationDeg, setRotationDeg] = useState(startDeg);

  useEffect(() => {
    setRotationDeg(startDeg);
    const frame = requestAnimationFrame(() => {
      setRotationDeg(targetDeg);
    });
    return () => cancelAnimationFrame(frame);
  }, [startDeg, targetDeg]);

  return (
    <g
      className="gauge-needle"
      style={{
        transform: `rotate(${rotationDeg}deg)`,
        transformOrigin: `${CENTER}px ${CENTER}px`,
        transition: "transform 0.9s cubic-bezier(0.22, 1.1, 0.36, 1)",
      }}
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
    >
      {/* Needle body */}
      <path
        d={`
          M ${CENTER - needleWidth / 2} ${CENTER}
          L ${CENTER} ${CENTER - needleLength}
          L ${CENTER + needleWidth / 2} ${CENTER}
          Z
        `}
        fill={color}
        stroke={color}
        strokeWidth={1}
        style={{ cursor: "pointer" }}
      />
    </g>
  );
};

// Center hub drawn once on top of all needles
const CenterHub: React.FC = () => (
  <g>
    <circle cx={CENTER} cy={CENTER} r={28} fill="#5f5f5f" stroke="#fff" strokeWidth={2} />
    <circle cx={CENTER} cy={CENTER} r={20} fill="#fff" />
  </g>
);

// Arc segment component
const ArcSegment: React.FC<{
  startPercent: number;
  endPercent: number;
  color: string;
}> = ({ startPercent, endPercent, color }) => {
  return (
    <path
      d={createArc(startPercent, endPercent, INNER_RADIUS, OUTER_RADIUS)}
      fill={color}
      stroke="#fff"
      strokeWidth={1}
    />
  );
};

// Tick marks and labels
const TickMarks: React.FC = () => {
  return (
    <>
      {ticks.map((tick) => {
        const angle = percentToAngle(tick);
        const outerPoint = polarToCartesian(angle, OUTER_RADIUS + 8);
        const innerPoint = polarToCartesian(angle, OUTER_RADIUS + 2);
        const labelPoint = polarToCartesian(angle, OUTER_RADIUS + 22);

        return (
          <g key={tick}>
            <line
              x1={innerPoint.x}
              y1={innerPoint.y}
              x2={outerPoint.x}
              y2={outerPoint.y}
              stroke="#8d8d8d"
              strokeWidth={2}
            />
            <text
              x={labelPoint.x}
              y={labelPoint.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#8d8d8d"
              fontSize={12}
              fontFamily="helvetica, arial, sans-serif"
            >
              {tick}
            </text>
          </g>
        );
      })}
    </>
  );
};

// Segment labels positioned on the arc
const SegmentLabels: React.FC = () => {
  return (
    <>
      {certificationStops.map((segment) => {
        const midPercent = (segment.start + segment.stop) / 2;
        const angle = percentToAngle(midPercent);
        const labelRadius = (INNER_RADIUS + OUTER_RADIUS) / 2;
        const point = polarToCartesian(angle, labelRadius);
        const isDark = segment.start >= 60;

        return (
          <text
            key={segment.label}
            x={point.x}
            y={point.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={isDark ? "#e3e3e3" : "#494949"}
            fontSize={11}
            fontWeight={600}
            fontFamily="helvetica, arial, sans-serif"
            style={{ pointerEvents: "none" }}
          >
            {segment.label.split(" ").map((word, i) => (
              <tspan key={i} x={point.x} dy={i === 0 ? 0 : 12}>
                {word}
              </tspan>
            ))}
          </text>
        );
      })}
    </>
  );
};

// Legend item with toggle
const LegendItem: React.FC<{
  pointer: Pointer;
  isVisible: boolean;
  onToggle: () => void;
  isHovered: boolean;
}> = ({ pointer, isVisible, onToggle, isHovered }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      color: "#636363",
      cursor: "pointer",
      opacity: isVisible ? 1 : 0.4,
      backgroundColor: isHovered ? "#f0f0f0" : "transparent",
      padding: "4px 8px",
      borderRadius: 4,
      transition: "all 0.2s ease",
    }}
    onClick={onToggle}
  >
    <span
      style={{
        width: 14,
        height: 14,
        background: pointer.color,
        display: "inline-block",
        borderRadius: 2,
      }}
    />
    <span style={{ fontSize: 15 }}>{pointer.label}</span>
    <strong style={{ color: "#464646" }}>{pointer.value}%</strong>
  </div>
);

// Tooltip component
const Tooltip: React.FC<{
  content: string;
  x: number;
  y: number;
}> = ({ content, x, y }) => (
  <div
    style={{
      position: "absolute",
      left: x,
      top: y,
      transform: "translate(-50%, -100%)",
      backgroundColor: "rgba(0, 0, 0, 0.85)",
      color: "#fff",
      padding: "8px 12px",
      borderRadius: 4,
      fontSize: 13,
      fontWeight: 500,
      pointerEvents: "none",
      whiteSpace: "nowrap",
      zIndex: 100,
    }}
  >
    {content}
  </div>
);

const GaugeChartSVG: React.FC<GaugeChartProps> = ({ valueArray, maxValue, valueType }) => {
  const [showOptionalPoints] = useLocalStorage("optionalBREEAMDial", false);
  const [visibilityOverrides, setVisibilityOverrides] = useState<Record<string, boolean>>({});
  const [hoveredPointer, setHoveredPointer] = useState<Pointer | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const denominator = maxValue > 0 ? maxValue : 1;
  const isCertification = valueType === "certification";

  const pointers: Pointer[] = useMemo(() => {
    if (isCertification) {
      return [
        {
          color: "#f59426",
          label: "Status sertifiseringsgrad",
          value: toPercent(valueArray[0], denominator),
          large: true,
        },
        {
          color: "#000000",
          label: "Antatt oppnådde poeng",
          value: toPercent(valueArray[1], denominator),
        },
        {
          color: "#7a7981",
          label: "Uavklarte poeng",
          value: toPercent(valueArray[2], denominator),
          visible: showOptionalPoints,
        },
      ];
    }
    return [
      { color: "green", label: "Antatt oppnås", value: toPercent(valueArray[0], denominator) },
      { color: "#FFEA00", label: "Krever noe", value: toPercent(valueArray[1], denominator) },
      { color: "orange", label: "Krever mer", value: toPercent(valueArray[2], denominator) },
      { color: "red", label: "Krever mye", value: toPercent(valueArray[3], denominator) },
    ];
  }, [valueArray, denominator, isCertification, showOptionalPoints]);

  const getEffectiveVisibility = (pointer: Pointer): boolean => {
    if (pointer.visible === false) return false;
    return visibilityOverrides[pointer.label] !== false;
  };

  const toggleVisibility = (label: string) => {
    setVisibilityOverrides((prev) => ({
      ...prev,
      [label]: prev[label] === false ? true : false,
    }));
  };

  const visiblePointers = pointers.filter((p) => p.visible !== false);
  const renderedPointers = visiblePointers.filter(getEffectiveVisibility);

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  return (
    <div
      style={{
        width: GAUGE_SIZE,
        minHeight: 420,
        fontFamily: "helvetica, arial, Verdana, sans-serif",
        margin: "0 auto",
      }}
    >
      <h4 style={{ textAlign: "center", color: "#464646", marginBottom: 0 }}>
        BREEAM {isCertification ? "Infrastructure sertifiseringsgrad" : "Preanalyse"}
      </h4>

      <div style={{ height: 320, position: "relative" }} onMouseMove={handleMouseMove}>
        <svg
          width={GAUGE_SIZE}
          height={300}
          viewBox={`0 0 ${GAUGE_SIZE} ${GAUGE_SIZE * 0.65}`}
          style={{ display: "block", margin: "0 auto" }}
        >
          {/* Arc segments */}
          <g>
            {certificationStops.map((segment) => (
              <ArcSegment
                key={segment.label}
                startPercent={segment.start}
                endPercent={segment.stop}
                color={segment.color}
              />
            ))}
          </g>

          {/* Tick marks */}
          <TickMarks />

          {/* Segment labels */}
          <SegmentLabels />

          {/* Needles - render in reverse order so primary is on top */}
          {[...renderedPointers].reverse().map((pointer) => (
            <Needle
              key={pointer.label}
              value={pointer.value}
              color={pointer.color}
              large={pointer.large}
              onHover={(hovering) => setHoveredPointer(hovering ? pointer : null)}
            />
          ))}

          {/* Center hub */}
          <CenterHub />
        </svg>

        {/* Tooltip */}
        {hoveredPointer && (
          <Tooltip content={`${hoveredPointer.label}: ${hoveredPointer.value}%`} x={mousePos.x} y={mousePos.y - 10} />
        )}
      </div>

      {/* Legend */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: "0.5rem 1rem",
        }}
      >
        {visiblePointers.map((pointer) => (
          <LegendItem
            key={pointer.label}
            pointer={pointer}
            isVisible={getEffectiveVisibility(pointer)}
            onToggle={() => toggleVisibility(pointer.label)}
            isHovered={hoveredPointer?.label === pointer.label}
          />
        ))}
      </div>
    </div>
  );
};

const toPercent = (value: number, maxValue: number) =>
  Math.max(0, Math.min(100, Math.round(((Number(value) || 0) / maxValue) * 100)));

export default GaugeChartSVG;
